import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../api/auth";
import { ApiError } from "../api/client";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { RisographBackground } from "../components/RisographBackground";
import { useAuth } from "../hooks/useAuth";

export function RegisterPage() {
	const { login: setToken } = useAuth();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const {
				access_token,
				name: userName,
				language,
				onboarding_complete,
			} = await register(email, password, name);
			setToken(access_token, userName, language, onboarding_complete);
			navigate("/onboarding/prompts");
		} catch (err) {
			if (err instanceof ApiError && err.status === 409) {
				setError(t("auth.register.errorConflict"));
			} else {
				setError(t("auth.register.errorGeneric"));
			}
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen flex items-center justify-center px-6">
			<RisographBackground />
			<div className="w-full max-w-md">
				<h1 className="font-display text-5xl font-bold text-ink mb-2 leading-tight">
					{t("auth.register.headline")}
				</h1>
				<p className="font-body italic text-ink/60 text-lg mb-10">
					{t("auth.register.subheadline")}
				</p>

				<form onSubmit={handleSubmit} className="space-y-8">
					<div className="border-b border-ink/30 pb-1">
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("auth.register.namePlaceholder")}
							required
							className="w-full bg-transparent font-body text-lg text-ink placeholder-ink/30 outline-none"
						/>
					</div>
					<div className="border-b border-ink/30 pb-1">
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={t("auth.register.emailPlaceholder")}
							required
							className="w-full bg-transparent font-body text-lg text-ink placeholder-ink/30 outline-none"
						/>
					</div>
					<div className="border-b border-ink/30 pb-1">
						<input
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder={t("auth.register.passwordPlaceholder")}
							required
							minLength={8}
							className="w-full bg-transparent font-body text-lg text-ink placeholder-ink/30 outline-none"
						/>
					</div>

					{error && (
						<p className="font-ui text-sm text-coral uppercase tracking-wide">
							{error}
						</p>
					)}

					<button
						type="submit"
						disabled={loading}
						className="ink-btn w-full py-3 font-ui text-sm uppercase tracking-widest"
					>
						{loading
							? t("auth.register.submitLoading")
							: t("auth.register.submitIdle")}
					</button>
				</form>

				<p className="font-ui text-xs uppercase tracking-widest text-ink/40 mt-8 text-center">
					{t("auth.register.alreadyHaveAccount")}{" "}
					<Link
						to="/login"
						className="text-ink/70 underline underline-offset-2"
					>
						{t("auth.register.signIn")}
					</Link>
				</p>

				<div className="mt-8 flex justify-center">
					<LanguageSwitcher />
				</div>
			</div>
		</div>
	);
}
